import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IIntegrationUIOption } from '@feathr/components';
import { toast } from '@feathr/components';
import { errorMessage } from '@feathr/hooks';

interface ILazyOptionsArgs<T extends IIntegrationUIOption> {
  /** Loads the options. Only called while `enabled` is true. */
  load: () => Promise<readonly T[]>;
  /** The error toast's copy, given the failed load's message. */
  errorLabel: (error: string) => string;
  /** Whether the options are needed yet. */
  enabled: boolean;
}

/**
 * Select options loaded the first time `enabled` is true, so a request that needs a Blackbaud token
 * never runs before it can succeed. `undefined` while loading. A failed load toasts `errorLabel`,
 * gives `[]` and retries the next time `enabled` turns true.
 */
export function useLazyOptions<T extends IIntegrationUIOption>({
  enabled,
  errorLabel,
  load,
}: ILazyOptionsArgs<T>): readonly T[] | undefined {
  const { t } = useTranslation();
  const [options, setOptions] = useState<readonly T[]>();
  const [hasFailed, setHasFailed] = useState(false);
  // The callbacks change identity every render; the effect should run for `enabled` only.
  const callbacks = useRef({ errorLabel, load });
  callbacks.current = { errorLabel, load };

  // `options` and `hasFailed` are left out on purpose: the effect sets them.
  useEffect(() => {
    if (!enabled || (options && !hasFailed)) {
      return;
    }
    // Ignore a response that lands after unmount or after `enabled` turns false.
    let isCurrent = true;

    async function loadOptions(): Promise<void> {
      try {
        const loaded = await callbacks.current.load();
        if (isCurrent) {
          setOptions(loaded);
          setHasFailed(false);
        }
      } catch (error) {
        if (isCurrent) {
          setOptions([]);
          setHasFailed(true);
          toast(callbacks.current.errorLabel(errorMessage(error, t)), { type: 'error' });
        }
      }
    }

    void loadOptions();
    return (): void => {
      isCurrent = false;
    };
  }, [enabled]);

  return options;
}
